/**
 * Notes - the matter's key facts, as a table with a source split.
 *
 * Two problems, one cause. The list rendered every note as a label on the left
 * and its value pushed to the right-hand edge of the row, which is the least
 * readable way to present a pair; and a fact the AI extracted from a case file
 * was indistinguishable from one a lawyer typed, even though only one of those
 * is something you'd trust without checking.
 *
 * Now: the shared `Table` (`notes/MatterNotesTable`), with the value beside its
 * label in a column wide enough to read, plus a Source column and - WHEN THE
 * PAYLOAD CAN ACTUALLY TELL THEM APART - an All / Added by us / AI generated
 * split on the tab bar. Where it cannot, the split is not offered: a grouping
 * built on a guess is worse than no grouping. See `notes/noteModel`.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Button from '../../shared/TailwindComponents/Button';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { useMatterSurface } from '../surface/MatterSurfaceContext';
import { noteId } from '../surface/detail/MatterItemDetail';
import MatterNotesTable from './notes/MatterNotesTable';
import { hasOriginSignal, noteOrigin, noteValueString } from './notes/noteModel';
import {
  PiNoteLight,
  PiTagLight,
  PiUserLight,
  PiSparkleLight
} from 'react-icons/pi';
import { MatterData } from '../MatterDetails';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, notesItemsTourSteps } from '../../shared/FeatureTour';

/** Which slice of the notes the tab bar is showing. */
type NotesOriginTab = 'all' | 'user' | 'ai';

export interface NoteItem {
  label: string;
  value: string;
  type: 'short_text' | 'long_text' | 'number' | 'date' | 'boolean';
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

interface NotesTabContentProps {
  matterData: MatterData | null;
  notes: NoteItem[];
  setNotes: React.Dispatch<React.SetStateAction<NoteItem[]>>;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  loading?: boolean;
  hasDocuments?: boolean;
}

const NotesTabContent: React.FC<NotesTabContentProps> = ({
  matterData,
  notes,
  setNotes,
  userAccessLevel,
  onUpdate,
  loading = false,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const { matterId } = useParams<{ matterId: string }>();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';
  const surface = useMatterSurface();

  /** Add → the shared container, empty, in create mode. */
  const openCreate = () => {
    if (!canEdit) return;
    surface?.select({ kind: 'note', id: null, mode: 'create' });
  };
  const matterUuid = matterData?.uuid || matterData?.MatterID || matterId || '';
  const [searchTerm, setSearchTerm] = useState('');
  const [originTab, setOriginTab] = useState<NotesOriginTab>('all');

  const { isGenerating: isFindingMore, handleGenerateMore: handleFindMoreNotes, progress: findMoreProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'notes',
    onComplete: onUpdate,
  });

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'notes-items',
    steps: notesItemsTourSteps,
    readyToShow: notes.length > 0 && !loading,
    autoStartDelay: 600,
  });

  const isTabLoading = loading;


  /* Value reading, type detection and origin all live in `notes/noteModel`,
     so the table, the tab counts and the search agree about what a note says. */

  const originCounts = useMemo(() => {
    const counts = { user: 0, ai: 0 };
    notes.forEach((note) => {
      const origin = noteOrigin(note);
      if (origin === 'user') counts.user += 1;
      if (origin === 'ai') counts.ai += 1;
    });
    return counts;
  }, [notes]);

  /* The split is offered only when the payload can actually make it. */
  const canSplitByOrigin = useMemo(() => hasOriginSignal(notes), [notes]);

  const activeTab: NotesOriginTab = canSplitByOrigin ? originTab : 'all';

  const filteredNotes = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return notes.filter((note) => {
      if (activeTab !== 'all' && noteOrigin(note) !== activeTab) return false;
      if (!term) return true;
      return String(note.label || '').toLowerCase().includes(term)
        || noteValueString(note.value).toLowerCase().includes(term);
    });
  }, [notes, searchTerm, activeTab]);

  const originTabs = canSplitByOrigin
    ? [
      {
        id: 'all' as NotesOriginTab,
        label: t('All notes'),
        icon: <PiTagLight className="w-3 h-3" />,
        count: notes.length,
        color: 'blue' as BadgeColor,
      },
      {
        id: 'user' as NotesOriginTab,
        label: t('Added by us'),
        icon: <PiUserLight className="w-3 h-3" />,
        count: originCounts.user,
        color: 'gray' as BadgeColor,
      },
      {
        id: 'ai' as NotesOriginTab,
        label: t('AI generated'),
        icon: <PiSparkleLight className="w-3 h-3" />,
        count: originCounts.ai,
        color: 'amber' as BadgeColor,
      },
    ]
    : [{ id: 'all' as NotesOriginTab, label: t('Notes'), icon: <PiTagLight className="w-3 h-3" /> }];

  if (isTabLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="animate-fadePulse bg-surface-sunken h-8 w-48 rounded" />
          <div className="animate-fadePulse bg-surface-sunken h-8 w-24 rounded" />
        </div>
        <Skeleton variant="table" rows={5} />
      </div>
    );
  }

  if (!matterData) {
    return (
      <div className="p-6 text-center text-fg-muted">
        {t('Loading notes...')}
      </div>
    );
  }

  /* FULL HEIGHT, NOT CONTENT HEIGHT.
     The tab used to be a `space-y-2` stack, so the table sized itself to its
     rows (capped at a hand-computed `100vh-360px`) and left dead space under
     it while the Context pane ran to the bottom of the viewport. The tab is
     now a flex column: the tab bar and the search row keep their height, the
     table area takes `flex-1 min-h-0` and the table fills what it is given —
     the same arrangement every other full-height list surface uses. */
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div data-tour-id="notes-actions" className="flex-shrink-0">
      <SubTabBar
        tabs={originTabs}
        activeTab={activeTab}
        onTabChange={(id) => setOriginTab(id as NotesOriginTab)}
        actions={canEdit && !isFindingMore ? (
          <>
            <Button
              variant="outline"
              size="small"
              onClick={openCreate}
            >
              {t('Add note')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleFindMoreNotes}
              disabled={!hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={<PiSparkleLight className="h-3.5 w-3.5" />}
            >
              {notes.length === 0 ? t('Generate') : t('Find more')}
            </Button>
          </>
        ) : undefined}
      />
      </div>

      {isFindingMore || findMoreProgress.status === 'complete' || findMoreProgress.status === 'failed' ? (
        <FindMoreProgress progress={findMoreProgress} isGenerating={isFindingMore} />
      ) : (
      <div className="flex flex-1 min-h-0 flex-col">
      <div className="flex-shrink-0">
      <FilterBarWithTabs
        onSearchChange={setSearchTerm}
        noParentPadding={true}
        noParentHeight={true}
        itemCount={filteredNotes.length}
        totalItemCount={notes.length}
        itemCountLabel={notes.length === 1 ? t('note') : t('notes')}
        />
      </div>

      {notes.length > 0 ? (
        <div data-tour-id="notes-table-area" className="flex-1 min-h-0 pt-2">
          <MatterNotesTable
            notes={filteredNotes}
            showSource={canSplitByOrigin}
            selectedId={surface?.selection?.kind === 'note' ? surface.selection.id : null}
            onSelectNote={(note) => surface?.select({
              kind: 'note', id: noteId(note), mode: 'edit',
            })}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
          <div className="h-12 w-12 rounded-full bg-surface-sunken flex items-center justify-center mb-3">
            <PiNoteLight className="h-6 w-6 text-champagne" />
          </div>
          <h3 className="text-fg-body font-medium mb-1">{t('No notes yet')}</h3>
          <p className="text-sm text-fg-muted max-w-md">
            {t('Key facts pulled out of this matter appear here. Add one yourself, or generate them from the case files.')}
          </p>
          {canEdit && (
            <div className="flex items-center gap-2 mt-4">
              <Button variant="outline" size="small" onClick={openCreate}>
                {t('Add note')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleFindMoreNotes}
                disabled={!hasDocuments}
                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={<PiSparkleLight className="h-3.5 w-3.5" />}
              >
                {t('Generate')}
              </Button>
            </div>
          )}
        </div>
      )}
      </div>
      )}
      <FeatureTour
        steps={notesItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default NotesTabContent;
